import { createContext, useContext, useState, useEffect } from 'react';
import { getColorTheme, saveColorTheme, COLOR_THEMES } from '@/utils/storage';
import { applyThemeIcon } from '@/utils/themeIcon';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(getColorTheme());

  useEffect(() => {
    // Apply theme CSS variables to root
    const themeColors = COLOR_THEMES[theme] || COLOR_THEMES.midnightblue;
    const root = document.documentElement;
    
    root.style.setProperty('--theme-primary', themeColors.primary);
    root.style.setProperty('--theme-secondary', themeColors.secondary);
    root.style.setProperty('--theme-accent', themeColors.accent);
    root.style.setProperty('--theme-light', themeColors.light);
    
    // Also set derived colors for buttons and UI
    root.style.setProperty('--theme-btn-bg', themeColors.primary);
    root.style.setProperty('--theme-btn-hover', themeColors.secondary);
    root.style.setProperty('--theme-border', themeColors.secondary);
    root.style.setProperty('--theme-input-bg', `${themeColors.light}80`); // with opacity
    root.style.setProperty('--theme-text-primary', themeColors.textPrimary || '#2D2D35');
    root.style.setProperty('--theme-text-secondary', themeColors.textSecondary || '#73737D');
    root.style.setProperty('--theme-logo-text', themeColors.logoText || '#F9F9FF');
    
    // Update body class for theme-specific styling
    document.body.setAttribute('data-theme', theme);

    // Keep iOS app icon in sync with the selected theme.
    void applyThemeIcon(theme);
  }, [theme]);

  const changeTheme = (newTheme) => {
    setTheme(newTheme);
    saveColorTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, changeTheme, themes: COLOR_THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;
